import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";

/** Starting Elo used across the app */
export const SKILL_BAR_MIN = 1200;

/** Fallback ceiling if global max cannot be loaded (display only) */
export const SKILL_BAR_FALLBACK_MAX = 2400;

let cachedMax = null;
let cachedAt = 0;
const CACHE_MS = 5 * 60 * 1000;

/**
 * Highest Skill among all athletes (any league/division). Cached briefly to limit reads.
 * @param {import('firebase/firestore').Firestore} db
 * @returns {Promise<number>}
 */
export async function fetchGlobalMaxSkill(db) {
  const now = Date.now();
  if (cachedMax != null && now - cachedAt < CACHE_MS) {
    return cachedMax;
  }
  const q = query(
    collection(db, "athletes"),
    orderBy("Skill", "desc"),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) {
    cachedMax = SKILL_BAR_MIN;
    cachedAt = now;
    return SKILL_BAR_MIN;
  }
  const raw = snap.docs[0].data().Skill;
  const n = typeof raw === "number" ? raw : Number(raw);
  const val = Number.isFinite(n) && n >= SKILL_BAR_MIN ? n : SKILL_BAR_MIN;
  cachedMax = val;
  cachedAt = now;
  return val;
}

export function invalidateGlobalMaxSkillCache() {
  cachedMax = null;
  cachedAt = 0;
}

/**
 * Lowest and highest Skill among athletes in one RankLeague (division).
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} rankLeague
 * @returns {Promise<{ min: number | null, max: number | null }>}
 */
export async function fetchLeagueSkillMinMax(db, rankLeague) {
  const league = String(rankLeague || "").trim();
  if (!league) {
    return { min: null, max: null };
  }
  const athletesRef = collection(db, "athletes");
  const parseSkill = (snap) => {
    if (snap.empty) return null;
    const raw = snap.docs[0].data().Skill;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  try {
    const [minSnap, maxSnap] = await Promise.all([
      getDocs(
        query(
          athletesRef,
          where("RankLeague", "==", league),
          orderBy("Skill", "asc"),
          limit(1)
        )
      ),
      getDocs(
        query(
          athletesRef,
          where("RankLeague", "==", league),
          orderBy("Skill", "desc"),
          limit(1)
        )
      ),
    ]);
    let minV = parseSkill(minSnap);
    let maxV = parseSkill(maxSnap);
    if (minV == null && maxV != null) minV = maxV;
    if (maxV == null && minV != null) maxV = minV;
    if (minV == null || maxV == null) {
      return { min: null, max: null };
    }
    if (maxV < minV) {
      const t = minV;
      minV = maxV;
      maxV = t;
    }
    if (maxV === minV) {
      maxV = minV + 1;
    }
    return { min: minV, max: maxV };
  } catch (_) {
    return { min: null, max: null };
  }
}

/**
 * Scale top end for the bar: global max from Firestore, but never below the
 * highest skill shown on screen (so a true #1 is never stuck at a tiny fill).
 * @param {number|null|undefined} loadedGlobalMax - from fetchGlobalMaxSkill, or null on error
 * @param {...number|null|undefined} skills - current wrestler(s)’ skill values on this UI
 * @returns {number}
 */
export function computeEffectiveSkillBarMax(loadedGlobalMax, ...skills) {
  const minTop = SKILL_BAR_MIN + 1;
  const loaded = Number(loadedGlobalMax);
  const nums = skills
    .map((v) => (typeof v === "number" ? v : Number(v)))
    .filter((n) => Number.isFinite(n));
  const maxOnScreen = nums.length ? Math.max(...nums) : SKILL_BAR_MIN;
  if (Number.isFinite(loaded)) {
    return Math.max(loaded, maxOnScreen, minTop);
  }
  return Math.max(maxOnScreen, minTop);
}

/**
 * Linear bar scale from league (RankLeague) min/max when known, plus every
 * on-screen skill so the bar never clips.
 * @returns {{ low: number, high: number }}
 */
export function computeEffectiveSkillBarRange(leagueMin, leagueMax, ...skills) {
  const nums = skills
    .map((v) => (typeof v === "number" ? v : Number(v)))
    .filter((n) => Number.isFinite(n));
  const lows = [];
  const highs = [];
  if (leagueMin != null && Number.isFinite(Number(leagueMin))) {
    lows.push(Number(leagueMin));
  }
  if (leagueMax != null && Number.isFinite(Number(leagueMax))) {
    highs.push(Number(leagueMax));
  }
  nums.forEach((n) => {
    lows.push(n);
    highs.push(n);
  });
  let low = lows.length ? Math.min(...lows) : SKILL_BAR_MIN;
  let high = highs.length ? Math.max(...highs) : null;
  if (high == null) {
    high = low + 1;
  }
  if (high <= low) {
    high = low + 1;
  }
  return { low, high };
}

/**
 * Linear fill 0–100%: skill at low → 0%, at high → 100%.
 * If high is null (not loaded yet), returns 0.
 * @param {number} skill
 * @param {number|null} high - top of scale (e.g. division max or global max)
 * @param {number} [low=1200] - bottom of scale (default Elo floor)
 */
export function skillBarLinearFillPercent(skill, high, low = SKILL_BAR_MIN) {
  const s = typeof skill === "number" ? skill : Number(skill);
  if (!Number.isFinite(s)) return 0;
  if (high == null) return 0;
  let hi = Number(high);
  if (!Number.isFinite(hi)) return 0;
  let lo = Number(low);
  if (!Number.isFinite(lo)) lo = SKILL_BAR_MIN;
  if (hi <= lo) {
    hi = lo + 1;
  }
  const pct = ((s - lo) / (hi - lo)) * 100;
  return Math.min(100, Math.max(0, pct));
}
